/**
 * Per-biome obstacle spawn logic. Each biome cycles through a fixed pattern
 * of obstacle types and positions so the player can develop a rhythm of
 * anticipation rather than react to noise.
 *
 * Direct port of the Kotlin `spawnMeadow`/`spawnReef`/etc. helpers in
 * `VerticalEngine.kt`.
 */
import {
  Biome,
  type Obstacle,
  type ObstacleBeehive,
  type ObstacleCoralSpire,
  type ObstacleDoubleGate,
  type ObstacleGate,
  type ObstacleJellyfish,
  type ObstacleKelpCurtain,
  type ObstacleLog,
  type ObstaclePulseWall,
} from './types';
import { obstacleDamage, obstacleRadius } from './colors';

const TAU = Math.PI * 2;

/** Normalize an obstacle to have computed `radius` and `damage`. */
function withDerived<T extends Obstacle>(o: T): T {
  return { ...o, radius: obstacleRadius(o), damage: obstacleDamage(o) } as T;
}

export function spawnMeadow(
  width: number,
  step: number,
  r: number,
  level: number,
  _x: number,
  y: number,
  seed: number,
  _rng: () => number,
): Obstacle {
  const gapWidth = Math.max(160 - level * 12, 110);
  const phrase = Math.floor(step / 4);
  const inPhrase = step % 4;
  const gapCenterX = (() => {
    switch (phrase % 3) {
      case 0:
        switch (inPhrase) {
          case 0: return width * 0.30;
          case 1: return width * 0.50;
          case 2: return width * 0.70;
          default: return width * 0.50;
        }
      case 1:
        switch (inPhrase) {
          case 0: return width * 0.30;
          case 1: return width * 0.70;
          case 2: return width * 0.30;
          default: return width * 0.70;
        }
      default:
        switch (inPhrase) {
          case 0: return width * 0.50;
          case 1: return width * 0.30;
          case 2: return width * 0.70;
          default: return width * 0.50;
        }
    }
  })();

  const isPhraseEnd = inPhrase === 3;
  if (isPhraseEnd && r < 0.34) {
    const o: ObstacleDoubleGate = {
      type: 'DOUBLE_GATE', x: width / 2, y, radius: 0, damage: 0, seed,
      gapCenterX, gapWidth, spacing: 190,
    };
    return withDerived(o);
  }
  if (r < 0.55) {
    const gs = Math.max(0, gapCenterX - gapWidth / 2);
    const o: ObstaclePulseWall = {
      type: 'PULSE_WALL', x: 0, y, radius: 0, damage: 0, seed,
      totalWidth: width, gapStart: gs, gapWidth,
    };
    return withDerived(o);
  }
  if (r < 0.90) {
    const o: ObstacleGate = {
      type: 'GATE', x: width / 2, y, radius: 0, damage: 0, seed,
      gapCenterX, gapWidth, pillarHalfWidth: 28, pillarHeight: 220,
    };
    return withDerived(o);
  }
  if (r < 0.95) {
    const laneIdx = step % 3;
    const logX = laneIdx === 0 ? width * 0.5 : laneIdx === 1 ? width * 0.3 : width * 0.7;
    const logRotation = (step * 0.3) % (Math.PI * 2);
    const o: ObstacleLog = {
      type: 'LOG', x: logX, y, radius: 0, damage: 0, seed,
      width: 60, height: 120, rotation: logRotation,
    };
    return withDerived(o);
  }
  const spireX = gapCenterX < width * 0.5 ? width * 0.75 : width * 0.25;
  const o: ObstacleCoralSpire = {
    type: 'CORAL_SPIRE', x: spireX, y, radius: 0, damage: 0, seed,
    halfWidth: 38, height: 180,
  };
  return withDerived(o);
}

export function spawnReef(
  width: number,
  step: number,
  r: number,
  level: number,
  x: number,
  y: number,
  seed: number,
  rng: () => number,
): Obstacle {
  if (r < 0.10) {
    const slitCount = rng() < 0.5 ? 2 : 3;
    const slitWidth = Math.max(90 - level * 6, 60);
    const slits: Array<[number, number]> = [];
    const slots = width / slitCount;
    for (let i = 0; i < slitCount; i++) {
      const slotStart = i * slots;
      const slotEnd = (i + 1) * slots - slitWidth;
      const start = slotStart + rng() * Math.max(1, slotEnd - slotStart);
      slits.push([start, start + slitWidth]);
    }
    const o: ObstacleKelpCurtain = {
      type: 'KELP_CURTAIN', x: 0, y, radius: 0, damage: 0, seed,
      totalWidth: width, slits,
    };
    return withDerived(o);
  }
  if (r < 0.50) {
    const o: ObstacleJellyfish = {
      type: 'JELLYFISH', x, y, radius: 0, damage: 0, seed,
      driftPhase: rng() * TAU,
      driftAmplitude: 120 + rng() * 80,
      driftSpeed: 1.2 + level * 0.08,
    };
    return withDerived(o);
  }
  if (r < 0.60) {
    const laneIdx = step % 3;
    const spireX = laneIdx === 0 ? width * 0.5 : laneIdx === 1 ? width * 0.25 : width * 0.75;
    const o: ObstacleCoralSpire = {
      type: 'CORAL_SPIRE', x: spireX, y, radius: 0, damage: 0, seed,
      halfWidth: 38, height: 180,
    };
    return withDerived(o);
  }
  if (r < 0.95) {
    const laneIdx = step % 3;
    const laneX = laneIdx === 0 ? width * 0.3 : laneIdx === 1 ? width * 0.5 : width * 0.7;
    const o: ObstacleBeehive = {
      type: 'BEEHIVE', x: laneX, y, radius: 0, damage: 0, seed,
      ringRadius: 50 + rng() * 30,
      beeCount: 5 + Math.floor(rng() * 4),
      phase: rng() * TAU,
      rotationSpeed: 0.8 + rng() * 0.6,
    };
    return withDerived(o);
  }
  const slitCount = rng() < 0.5 ? 2 : 3;
  const slitWidth = Math.max(90 - level * 6, 60);
  const slits: Array<[number, number]> = [];
  const slots = width / slitCount;
  for (let i = 0; i < slitCount; i++) {
    const slotStart = i * slots;
    const slotEnd = (i + 1) * slots - slitWidth;
    const start = slotStart + rng() * Math.max(1, slotEnd - slotStart);
    slits.push([start, start + slitWidth]);
  }
  const kelp: ObstacleKelpCurtain = {
    type: 'KELP_CURTAIN', x: 0, y, radius: 0, damage: 0, seed,
    totalWidth: width, slits,
  };
  return withDerived(kelp);
}

export function spawnLattice(
  width: number,
  step: number,
  _r: number,
  level: number,
  _x: number,
  y: number,
  seed: number,
  rng: () => number,
): Obstacle {
  const phrase = Math.floor(step / 3);
  const inPhrase = step % 3;
  const gapWidth = Math.max(88 - level * 4, 56);

  switch (phrase % 3) {
    case 0: {
      const gapCount = 3;
      const gaps: Array<[number, number]> = [];
      const slots = width / gapCount;
      for (let i = 0; i < gapCount; i++) {
        const slotStart = i * slots;
        const slotEnd = (i + 1) * slots - gapWidth;
        const start = slotStart + rng() * Math.max(1, slotEnd - slotStart);
        gaps.push([start, start + gapWidth]);
      }
      const o = {
        type: 'PORTAL_WALL' as const,
        x: 0, y, radius: 0, damage: 0, seed,
        totalWidth: width,
        gaps,
      };
      return withDerived(o);
    }
    case 1: {
      const angle = Math.floor(phrase / 3) % 2 === 0 ? Math.PI / 4 : -Math.PI / 4;
      const o = {
        type: 'DIAGONAL_BEAM' as const,
        x: width / 2, y, radius: 0, damage: 0, seed,
        length: 380, thickness: 16, angle,
      };
      return withDerived(o);
    }
    default: {
      const cx = inPhrase === 0 ? width * 0.30 : inPhrase === 1 ? width * 0.70 : width * 0.50;
      const o = {
        type: 'CROSS_GATE' as const,
        x: cx, y, radius: 0, damage: 0, seed,
        armLength: 140 + level * 2,
        armThickness: 20,
        phase: rng() * TAU,
        rotationSpeed: 0.15 + level * 0.01,
      };
      return withDerived(o);
    }
  }
}

export function spawnMaelstrom(
  width: number,
  step: number,
  _r: number,
  level: number,
  _x: number,
  y: number,
  seed: number,
  rng: () => number,
): Obstacle {
  const phrase = Math.floor(step / 2);
  const inPhrase = step % 2;
  switch (phrase % 3) {
    case 0: {
      const gapWidth = Math.max(130 - level * 2, 100);
      const o = {
        type: 'SHIFTING_WALL' as const,
        x: 0, y, radius: 0, damage: 0, seed,
        totalWidth: width,
        gapWidth,
        phase: rng() * TAU,
        amplitude: (width - gapWidth) * 0.25,
        speed: 0.001,
      };
      return withDerived(o);
    }
    case 1: {
      if (inPhrase === 0) {
        const o = {
          type: 'CHAOS_RING' as const,
          x: 0, y, radius: 0, damage: 0, seed,
          totalWidth: width,
          innerGapWidth: 70,
          outerGapWidth: 70,
          spacing: 180,
          innerPhase: rng() * TAU,
          outerPhase: rng() * TAU,
          slideSpeed: 0.004,
        };
        return withDerived(o);
      }
      const o = {
        type: 'COMET' as const,
        x: rng() < 0.5 ? -60 : width + 60,
        y: y - 1100, radius: 0, damage: 0, seed,
        velocityX: rng() < 0.5 ? 200 : -200,
        radiusBody: 18,
      };
      return withDerived(o);
    }
    default: {
      const o = {
        type: 'COMET' as const,
        x: phrase % 2 === 0 ? -80 : width + 80,
        y: y - 1100, radius: 0, damage: 0, seed,
        velocityX: phrase % 2 === 0 ? 220 : -220,
        radiusBody: 18,
      };
      return withDerived(o);
    }
  }
}

export function spawnObstacleForBiome(
  width: number,
  biome: Biome,
  level: number,
  y: number,
  patternStep: number,
  rng: () => number,
): Obstacle {
  const r = rng();
  const x = 60 + rng() * (width - 120);
  const seed = rng() * 1000;
  switch (biome) {
    case Biome.MEADOW:
      return spawnMeadow(width, patternStep, r, level, x, y, seed, rng);
    case Biome.REEF:
      return spawnReef(width, patternStep, r, level, x, y, seed, rng);
    case Biome.LATTICE:
      return spawnLattice(width, patternStep, r, level, x, y, seed, rng);
    case Biome.MAELSTROM:
      return spawnMaelstrom(width, patternStep, r, level, x, y, seed, rng);
  }
}
